import { useMemo } from 'react'
import { useAuth } from '@clerk/nextjs'

import { isApiError, path, useBackendClient } from '@/lib/backend-client'
import type { BackendClient } from '@/lib/backend-client/client'

import { screenFeatureErrorFrom } from './errors'
import type { ScreenFeatureResource, ScreenFeatureTransport } from './transport'

const RESOURCE_SEGMENT: Record<ScreenFeatureResource, string> = {
  screen: 'screen-features',
  global: 'global-features'
}

const endpointPath = (resource: ScreenFeatureResource, wireName: string) =>
  path`/v1/system/app-config/${RESOURCE_SEGMENT[resource]}/${wireName}`

/**
 * The HTTP adapter over plan 01's backend client. Same URLs as
 * `getGlobalFeature` / `updateGlobalFeature(Partial)` / `getScreenFeature` /
 * `updateScreenFeature` in `services/app-config.ts`.
 *
 * The TARGET school travels as the `school_id` query param; the backend
 * authorizes and resolves the row from it alone. The client's `x-school-id`
 * stays the admin's active school, which may differ on a super-admin
 * cross-school write (web refactor 00, decision 4).
 *
 * `send()`, not `request()` with the entry schema: under the client's
 * production `report` policy a schema mismatch returns the raw payload, which
 * would bypass the hook's own per-entry parse and its fail-closed read (KTD7).
 */
export function createHttpScreenFeatureTransport(client: BackendClient): ScreenFeatureTransport {
  const call = async (resource: ScreenFeatureResource, run: () => Promise<{ body: unknown }>) => {
    try {
      return (await run()).body
    } catch (error) {
      if (!isApiError(error)) throw error
      throw screenFeatureErrorFrom(resource, { status: error.status, type: error.type, message: error.message })
    }
  }

  return {
    async get(resource, wireName, schoolId) {
      const body = await call(resource, () =>
        client.send({ method: 'GET', path: endpointPath(resource, wireName), query: { school_id: schoolId } })
      )
      return (body as { data?: unknown } | undefined)?.data
    },

    async patch(resource, wireName, body, { schoolId, partial }) {
      await call(resource, () =>
        client.send({
          method: 'PATCH',
          path: endpointPath(resource, wireName),
          query: { school_id: schoolId, partial: partial ? 'true' : undefined },
          body
        })
      )
    }
  }
}

/**
 * The default transport: HTTP over plan 01's session client, which carries the
 * signed-in user's Clerk token and the active school as `x-school-id`. `null`
 * until Clerk has a user, so no read fires before sign-in.
 */
export function useHttpScreenFeatureTransport(): ScreenFeatureTransport | null {
  const { userId } = useAuth()
  const client = useBackendClient()
  return useMemo(() => (userId ? createHttpScreenFeatureTransport(client) : null), [client, userId])
}
